import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { BarChart, Bar, ResponsiveContainer, XAxis, Tooltip, Cell } from "recharts";
import {
    Flame,
    Trophy,
    Target,
    Clock,
    Award,
    TrendingUp,
    LogOut,
    Download,
    Trash2,
    AlertTriangle,
    X,
    Camera,
    Loader2,
    ChevronRight,
} from "lucide-react";
import Layout from "../components/Layout";
import ProgressRing from "../components/ProgressRing";
import BadgeIcon from "../components/BadgeIcon";
import StreakTimeline from "../components/StreakTimeline";
import NotificationSettingsCard from "../components/NotificationSettingsCard";
import MyAssociationsCard from "../components/MyAssociationsCard";
import api, { API_BASE } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import SocialEngagement from "../components/SocialEngagement";

const ALL_BADGES = [
    { badge_id: "badge_first", name: "Premier pas", icon: "star", color: "#FCDB53" },
    { badge_id: "badge_pitch", name: "Expert Pitch", icon: "mic", color: "#FCDB53" },
    { badge_id: "badge_streak", name: "Constance", icon: "flame", color: "#EE722E" },
    { badge_id: "badge_top", name: "Top Performer", icon: "crown", color: "#F89F63" },
    { badge_id: "badge_closer", name: "Closer", icon: "trophy", color: "#EE722E" },
    { badge_id: "badge_weekly_champion", name: "Champion semaine", icon: "crown", color: "#FCDB53" },
];

export default function Profil() {
    const { user, logout, setUser } = useAuth();
    const navigate = useNavigate();
    const [stats, setStats] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState("");
    const [deleting, setDeleting] = useState(false);
    const [uploadingAvatar, setUploadingAvatar] = useState(false);
    const avatarInputRef = useRef(null);

    useEffect(() => {
        api.get("/me/stats").then((r) => setStats(r.data));
    }, []);

    const onAvatarPick = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        if (!file.type.startsWith("image/")) {
            toast.error("Choisis une image (PNG / JPG / WebP)");
            return;
        }
        if (file.size > 5 * 1024 * 1024) {
            toast.error("Image trop lourde (max 5 Mo)");
            return;
        }
        setUploadingAvatar(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            fd.append("kind", "image");
            const up = await api.post("/upload", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            const url = `${API_BASE}/files/${up.data.storage_path}`;
            const profile = await api.patch("/me/profile", { avatar: url });
            setUser((u) => ({ ...(u || {}), avatar: profile.data.avatar }));
            toast.success("Photo de profil mise à jour");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Échec de l'upload");
        } finally {
            setUploadingAvatar(false);
        }
    };

    const earnedIds = new Set((stats?.badges || []).map((b) => b.badge_id));
    const allModulesDone =
        stats &&
        stats.total_modules > 0 &&
        stats.completed_modules === stats.total_modules;

    const downloadCertificate = async () => {
        try {
            const res = await fetch(`${API_BASE}/me/certificate`, {
                credentials: "include",
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                toast.error(err.detail || "Certificat non disponible");
                return;
            }
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `certificat-shariti-academy.pdf`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            toast.success("Certificat téléchargé");
        } catch {
            toast.error("Erreur de téléchargement");
        }
    };

    const handleDeleteAccount = async () => {
        if (deleteConfirm.trim().toUpperCase() !== "SUPPRIMER") {
            toast.error('Tape "SUPPRIMER" pour confirmer');
            return;
        }
        setDeleting(true);
        try {
            await api.delete("/me");
            setUser(false);
            toast.success("Ton compte a été supprimé");
            navigate("/login");
        } catch (err) {
            toast.error(
                err.response?.data?.detail || "Impossible de supprimer le compte",
            );
            setDeleting(false);
        }
    };

    return (
        <Layout title="Mon profil" subtitle="Tes stats personnelles">
            <div className="grid grid-cols-12 gap-4 lg:gap-6">
                {/* Profile card */}
                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="col-span-12 lg:col-span-5 rounded-3xl p-6 lg:p-8 relative overflow-hidden bg-gradient-to-br from-shariti-orange to-shariti-orange-light text-white"
                    data-testid="profile-header"
                >
                    <div className="absolute -bottom-10 -right-10 w-48 h-48 rounded-full bg-shariti-yellow/30 blur-3xl" />
                    <div className="relative z-10">
                        <div className="flex items-center gap-4 mb-6">
                            <button
                                type="button"
                                onClick={() => avatarInputRef.current?.click()}
                                disabled={uploadingAvatar}
                                data-testid="avatar-upload-button"
                                aria-label="Modifier ma photo de profil"
                                className="relative group shrink-0 rounded-3xl overflow-hidden focus:outline-none focus:ring-2 focus:ring-white/60 disabled:opacity-70"
                            >
                                {user?.avatar ? (
                                    <img
                                        src={user.avatar}
                                        alt={user.name}
                                        className="w-20 h-20 rounded-3xl object-cover"
                                    />
                                ) : (
                                    <div className="w-20 h-20 rounded-3xl bg-white/20 backdrop-blur flex items-center justify-center font-extrabold text-3xl">
                                        {user?.name?.[0]?.toUpperCase()}
                                    </div>
                                )}
                                {/* Hover overlay (desktop) + always-visible camera badge (mobile) */}
                                <span className="absolute inset-0 bg-black/55 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 group-focus:opacity-100 transition-opacity">
                                    {uploadingAvatar ? (
                                        <Loader2 className="w-5 h-5 text-white animate-spin" />
                                    ) : (
                                        <Camera className="w-5 h-5 text-white" />
                                    )}
                                </span>
                                <span className="absolute bottom-1 right-1 w-6 h-6 rounded-full bg-shariti-black/80 border border-white/40 flex items-center justify-center sm:hidden">
                                    {uploadingAvatar ? (
                                        <Loader2 className="w-3 h-3 text-white animate-spin" />
                                    ) : (
                                        <Camera className="w-3 h-3 text-white" />
                                    )}
                                </span>
                            </button>
                            <input
                                ref={avatarInputRef}
                                type="file"
                                accept="image/png,image/jpeg,image/jpg,image/webp"
                                className="hidden"
                                onChange={onAvatarPick}
                                data-testid="avatar-upload-input"
                            />
                            <div>
                                <p className="text-xs font-thin tracking-[0.3em] uppercase opacity-80 mb-1">
                                    {user?.role}
                                </p>
                                <h2 className="text-2xl lg:text-3xl font-extrabold tracking-tight">
                                    {user?.name}
                                </h2>
                                <p className="text-sm opacity-80 mt-0.5">{user?.email}</p>
                            </div>
                        </div>

                        <div className="grid grid-cols-3 gap-3 mb-6">
                            <div className="bg-shariti-black/20 backdrop-blur rounded-2xl p-3">
                                <Trophy className="w-4 h-4 mb-1" />
                                <p className="text-2xl font-extrabold">
                                    #{stats?.rank || "-"}
                                </p>
                                <p className="text-[10px] font-thin uppercase tracking-widest opacity-80">
                                    Rang
                                </p>
                            </div>
                            <div className="bg-shariti-black/20 backdrop-blur rounded-2xl p-3">
                                <Flame className="w-4 h-4 mb-1" />
                                <p className="text-2xl font-extrabold">
                                    {user?.streak_days || 0}
                                </p>
                                <p className="text-[10px] font-thin uppercase tracking-widest opacity-80">
                                    Série
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={() => navigate("/historique")}
                                data-testid="profile-points-clickable"
                                className="bg-shariti-black/20 backdrop-blur rounded-2xl p-3 text-left hover:bg-shariti-black/40 transition-all group cursor-pointer relative"
                                title="Voir l'historique détaillé"
                            >
                                <TrendingUp className="w-4 h-4 mb-1 group-hover:text-shariti-yellow transition-colors" />
                                <p className="text-2xl font-extrabold">
                                    {stats?.points || 0}
                                </p>
                                <p className="text-[10px] font-thin uppercase tracking-widest opacity-80 group-hover:opacity-100 inline-flex items-center gap-1">
                                    Points
                                    <ChevronRight className="w-3 h-3 opacity-70 group-hover:translate-x-0.5 transition-transform" />
                                </p>
                            </button>
                        </div>

                        <div className="flex flex-wrap gap-2">
                            <button
                                onClick={logout}
                                data-testid="profile-logout-button"
                                className="inline-flex items-center gap-2 bg-shariti-black/30 backdrop-blur text-white font-bold py-2 px-4 rounded-2xl hover:bg-shariti-black/50 transition-all"
                            >
                                <LogOut className="w-4 h-4" />
                                Déconnexion
                            </button>
                            <button
                                onClick={() => navigate("/regles")}
                                data-testid="profile-rules-link"
                                className="inline-flex items-center gap-2 bg-shariti-black/30 backdrop-blur text-white font-bold py-2 px-4 rounded-2xl hover:bg-shariti-black/50 transition-all"
                            >
                                <Award className="w-4 h-4" />
                                Règles des points
                            </button>
                        </div>
                    </div>
                </motion.div>

                {/* Streak Timeline (iter-25) — Duolingo-style 7-day strip */}
                {stats?.activity_7d?.length > 0 && (
                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="col-span-12"
                    >
                        <StreakTimeline
                            days={stats.activity_7d}
                            streak={user?.streak_days || stats?.streak_days || 0}
                        />
                    </motion.div>
                )}

                {/* Notifications settings (iter-26) */}
                <div className="col-span-12">
                    <NotificationSettingsCard />
                </div>

                {/* Certificate */}
                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className={`col-span-12 rounded-3xl p-6 lg:p-7 relative overflow-hidden ${
                        allModulesDone
                            ? "bg-gradient-to-br from-shariti-yellow/15 to-shariti-orange/10 border border-shariti-yellow/30"
                            : "glass-card"
                    }`}
                    data-testid="profile-certificate-card"
                >
                    <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                        <div className="flex items-center gap-4 flex-1 min-w-0">
                            <div
                                className={`w-14 h-14 rounded-2xl flex items-center justify-center shrink-0 ${
                                    allModulesDone
                                        ? "bg-shariti-yellow text-shariti-black"
                                        : "bg-white/5 text-white/40"
                                }`}
                            >
                                <Award className="w-7 h-7" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-xs font-thin tracking-[0.25em] uppercase text-shariti-orange-light mb-1">
                                    Certificat Shariti Academy
                                </p>
                                <h3 className="text-base sm:text-xl font-extrabold text-white tracking-tight leading-snug">
                                    {allModulesDone
                                        ? "Bravo, ton certificat est disponible 🎓"
                                        : `Termine tous les modules (${stats?.completed_modules || 0}/${stats?.total_modules || 0}) pour le débloquer`}
                                </h3>
                            </div>
                        </div>
                        <button
                            onClick={downloadCertificate}
                            disabled={!allModulesDone}
                            data-testid="profile-certificate-download"
                            className={`inline-flex items-center justify-center gap-2 font-extrabold py-3 px-5 rounded-2xl transition-all w-full sm:w-auto shrink-0 ${
                                allModulesDone
                                    ? "bg-shariti-orange text-white orange-glow hover:bg-shariti-orange-light"
                                    : "bg-white/5 text-white/40 cursor-not-allowed"
                            }`}
                        >
                            <Download className="w-4 h-4" />
                            Télécharger
                        </button>
                    </div>
                </motion.div>

                {/* Iter-27.21 — Mon affectation terrain (visible juste après l'identité) */}
                <div className="col-span-12">
                    <MyAssociationsCard variant="full" />
                </div>

                {/* Big progress — Technique + Objet social side-by-side (iter-9) */}
                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.05 }}
                    className="col-span-12 lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-3"
                    data-testid="profile-progress"
                >
                    {/* Technique */}
                    <div className="glass-card rounded-3xl p-5 lg:p-6 flex items-center gap-4">
                        <ProgressRing
                            value={stats?.overall_percent || 0}
                            size={108}
                            stroke={10}
                            label=""
                        />
                        <div className="flex-1 min-w-0">
                            <p className="text-[10px] font-thin tracking-[0.25em] uppercase text-shariti-orange-light mb-1">
                                Technique
                            </p>
                            <p className="text-xl font-extrabold text-white tracking-tight leading-tight">
                                {stats?.completed_modules || 0} / {stats?.total_modules || 0}
                            </p>
                            <p className="text-xs text-white/55 font-light">modules validés</p>
                            <div className="mt-2 grid grid-cols-2 gap-2 text-[11px]">
                                <div>
                                    <p className="text-white/40 uppercase tracking-widest font-thin">Score</p>
                                    <p className="font-extrabold text-white">
                                        {stats?.avg_score || 0}%
                                    </p>
                                </div>
                                <div>
                                    <p className="text-white/40 uppercase tracking-widest font-thin">Temps</p>
                                    <p className="font-extrabold text-white">
                                        {Math.floor((stats?.time_minutes || 0) / 60)}h
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Objet social */}
                    <div className="glass-card rounded-3xl p-5 lg:p-6 flex items-center gap-4 border border-shariti-yellow/15">
                        <ProgressRing
                            value={stats?.social_progress?.percent || 0}
                            size={108}
                            stroke={10}
                            label=""
                            color="#FCDB53"
                        />
                        <div className="flex-1 min-w-0">
                            <p className="text-[10px] font-thin tracking-[0.25em] uppercase text-shariti-yellow mb-1">
                                Objet social
                            </p>
                            <p className="text-xl font-extrabold text-white tracking-tight leading-tight">
                                {stats?.social_progress?.viewed || 0} /{" "}
                                {stats?.social_progress?.total_visible || 0}
                            </p>
                            <p className="text-xs text-white/55 font-light">associations explorées</p>
                            {(!stats?.social_progress?.total_visible) && (
                                <p className="mt-2 text-[10px] text-white/40 italic leading-snug">
                                    Aucune association attribuée. Demande à ton manager.
                                </p>
                            )}
                            {stats?.social_progress?.total_visible > 0 && (
                                <a
                                    href="/associations"
                                    className="mt-2 inline-flex items-center gap-1 text-[11px] font-bold text-shariti-yellow hover:underline"
                                >
                                    Voir mes associations →
                                </a>
                            )}
                        </div>
                    </div>
                </motion.div>

                {/* Activity */}
                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="col-span-12 lg:col-span-7 glass-card rounded-3xl p-6 lg:p-7"
                >
                    <p className="text-xs font-thin tracking-[0.25em] uppercase text-white/50 mb-1">
                        Activité 7 jours
                    </p>
                    <h3 className="text-xl font-extrabold text-white mb-5">
                        {stats?.activity_7d?.reduce((s, d) => s + d.value, 0) || 0} quiz cette semaine
                    </h3>
                    <div className="h-44 -mx-2">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={stats?.activity_7d || []}>
                                <XAxis
                                    dataKey="day"
                                    axisLine={false}
                                    tickLine={false}
                                    tick={{ fill: "#888", fontSize: 11, fontFamily: "Montserrat" }}
                                />
                                <Tooltip
                                    cursor={{ fill: "rgba(238,114,46,0.05)" }}
                                    contentStyle={{
                                        background: "#181818",
                                        border: "1px solid #EE722E",
                                        borderRadius: 12,
                                        fontFamily: "Montserrat",
                                    }}
                                    labelStyle={{ color: "#EE722E", fontWeight: 800, marginBottom: 4 }}
                                    itemStyle={{ color: "#fff", fontWeight: 700 }}
                                    formatter={(value) => [`${value} quiz`, null]}
                                    separator=""
                                />
                                <Bar dataKey="value" radius={[8, 8, 8, 8]}>
                                    {(stats?.activity_7d || []).map((_, i) => (
                                        <Cell
                                            key={i}
                                            fill={
                                                i === (stats?.activity_7d?.length || 0) - 1
                                                    ? "#EE722E"
                                                    : "#3a2a1f"
                                            }
                                        />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </motion.div>

                {/* Iter-16 — Social engagement (promote Shariti) */}
                <SocialEngagement
                    onPointsChanged={() => api.get("/me/stats").then((r) => setStats(r.data))}
                />

                {/* Badges */}
                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.15 }}
                    className="col-span-12 lg:col-span-5 glass-card rounded-3xl p-6 lg:p-7"
                    data-testid="profile-badges"
                >
                    <div className="flex items-center justify-between mb-5">
                        <div>
                            <p className="text-xs font-thin tracking-[0.25em] uppercase text-white/50 mb-1">
                                Collection
                            </p>
                            <h3 className="text-xl font-extrabold text-white">
                                Badges {earnedIds.size} / {ALL_BADGES.length}
                            </h3>
                        </div>
                        <Award className="w-6 h-6 text-shariti-yellow" />
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                        {ALL_BADGES.map((b) => {
                            const earned = earnedIds.has(b.badge_id);
                            return (
                                <div key={b.badge_id} className="flex flex-col items-center text-center">
                                    <BadgeIcon badge={b} size="lg" earned={earned} />
                                    <p
                                        className={`text-[11px] font-bold mt-2 ${
                                            earned ? "text-white" : "text-white/30"
                                        }`}
                                    >
                                        {b.name}
                                    </p>
                                </div>
                            );
                        })}
                    </div>
                </motion.div>

                {/* Danger zone */}
                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="col-span-12 rounded-3xl p-5 lg:p-6 border border-red-500/20 bg-red-500/5"
                    data-testid="danger-zone"
                >
                    <div className="flex items-center gap-3 flex-wrap">
                        <div className="w-10 h-10 rounded-2xl bg-red-500/15 flex items-center justify-center shrink-0">
                            <AlertTriangle className="w-5 h-5 text-red-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-xs font-thin tracking-[0.25em] uppercase text-red-400 mb-0.5">
                                Zone sensible
                            </p>
                            <h3 className="text-base font-extrabold text-white">
                                Supprimer mon compte
                            </h3>
                            <p className="text-xs text-white/50 mt-0.5">
                                Action définitive. Toutes tes données (progression, badges,
                                sessions IA, favoris...) seront effacées.
                            </p>
                        </div>
                        <button
                            onClick={() => setShowDeleteModal(true)}
                            data-testid="delete-account-button"
                            className="inline-flex items-center gap-2 bg-red-500/15 text-red-400 hover:bg-red-500 hover:text-white border border-red-500/30 font-bold py-2.5 px-4 rounded-2xl transition-all"
                        >
                            <Trash2 className="w-4 h-4" />
                            Supprimer
                        </button>
                    </div>
                </motion.div>
            </div>

            {/* Delete confirmation modal */}
            <AnimatePresence>
                {showDeleteModal && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => !deleting && setShowDeleteModal(false)}
                        className="fixed inset-0 z-[100] bg-shariti-black/85 backdrop-blur flex items-center justify-center p-4 pb-28 sm:pb-4 overflow-y-auto"
                        data-testid="delete-modal"
                    >
                        <motion.div
                            initial={{ y: 60, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            exit={{ y: 60, opacity: 0 }}
                            onClick={(e) => e.stopPropagation()}
                            className="w-full max-w-md bg-shariti-surface rounded-3xl p-5 sm:p-6 border border-red-500/30 my-auto"
                        >
                            <div className="flex items-start justify-between mb-4">
                                <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-2xl bg-red-500/20 flex items-center justify-center shrink-0">
                                    <AlertTriangle className="w-5 h-5 sm:w-6 sm:h-6 text-red-400" />
                                </div>
                                <button
                                    onClick={() => !deleting && setShowDeleteModal(false)}
                                    className="w-9 h-9 rounded-xl bg-white/5 flex items-center justify-center hover:bg-white/10 shrink-0"
                                >
                                    <X className="w-4 h-4 text-white" />
                                </button>
                            </div>
                            <h3 className="text-lg sm:text-xl font-extrabold text-white tracking-tight mb-2 leading-snug">
                                Supprimer définitivement ton compte ?
                            </h3>
                            <p className="text-xs sm:text-sm text-white/60 font-light mb-4 sm:mb-5 leading-relaxed">
                                Cette action est <strong className="text-red-400">irréversible</strong>.
                                Tu perdras toute ta progression, tes badges, ton historique,
                                tes sessions de simulateur IA et tes favoris.
                            </p>
                            <p className="text-xs text-white/50 mb-2">
                                Tape{" "}
                                <span className="font-mono font-bold text-red-400">
                                    SUPPRIMER
                                </span>{" "}
                                pour confirmer :
                            </p>
                            <input
                                value={deleteConfirm}
                                onChange={(e) => setDeleteConfirm(e.target.value)}
                                placeholder="SUPPRIMER"
                                data-testid="delete-confirm-input"
                                className="w-full bg-white/5 border border-red-500/30 rounded-2xl px-4 py-3 text-white placeholder-white/20 outline-none focus:border-red-500 mb-4 font-mono"
                            />
                            <div className="flex flex-col-reverse sm:flex-row gap-2">
                                <button
                                    onClick={() => !deleting && setShowDeleteModal(false)}
                                    disabled={deleting}
                                    className="flex-1 glass-card rounded-2xl py-3 font-bold text-white hover:border-white/30 disabled:opacity-50"
                                >
                                    Annuler
                                </button>
                                <button
                                    onClick={handleDeleteAccount}
                                    disabled={
                                        deleting ||
                                        deleteConfirm.trim().toUpperCase() !== "SUPPRIMER"
                                    }
                                    data-testid="delete-confirm-button"
                                    className="flex-1 inline-flex items-center justify-center gap-2 bg-red-500 text-white font-extrabold py-3 rounded-2xl hover:bg-red-600 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                    {deleting ? (
                                        <span className="w-4 h-4 rounded-full border-2 border-white border-t-transparent animate-spin" />
                                    ) : (
                                        <>
                                            <Trash2 className="w-4 h-4" />
                                            Supprimer
                                        </>
                                    )}
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </Layout>
    );
}
